"use client";

import { useEffect, useMemo, useRef, useState, type ReactNode, type Ref } from "react";
import { ArrowRight, Check, Minus, Mountain, Pencil, Plus, ShieldCheck, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RouteIcon } from "@/components/marketing/RouteIcon";
import { trackEvent } from "@/lib/analytics/track";
import { buildLoginSetupPath, defaultSignupRouteSlugs } from "@/lib/signup-flow";
import { cn } from "@/lib/utils";
import { DateField, parseYmdLocal, toYmd } from "@/components/marketing/AlertDateField";
import { useAlertSetupAbandonment } from "@/components/marketing/useAlertSetupAbandonment";
import { useElementViewTracking } from "@/components/marketing/useElementViewTracking";
import { COPY } from "@/content/i18n";
import { DEFAULT_LOCALE } from "@/lib/i18n";
import type { AttractionId, PlatformLocale } from "@/lib/attractions/types";

export type AlertSetupCopy = { [Key in keyof typeof COPY.en.alertSetup]: string };

function defaultCopy(locale: PlatformLocale): AlertSetupCopy {
  return locale === "en" || locale === "es" || locale === "pt"
    ? COPY[locale].alertSetup
    : COPY.en.alertSetup;
}

export type AlertSetupFieldRouteGroup = {
  id: string | number;
  name: string;
  routes: {
    value: string;
    name: string;
  }[];
};

export type AlertSetupRouteGroup = {
  id: string | number;
  name: string;
  routes: {
    id: string | number;
    name: string;
    param: string;
  }[];
};

type SectionKey = "routes" | "dates" | "party";

function SectionToggle({
  open,
  onClick,
  editLabel,
  doneLabel,
}: {
  open: boolean;
  onClick: () => void;
  editLabel: string;
  doneLabel: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex shrink-0 items-center gap-1 text-sm font-semibold text-brand-accent hover:underline"
    >
      {open ? (
        doneLabel
      ) : (
        <>
          <Pencil className="size-3.5" />
          {editLabel}
        </>
      )}
    </button>
  );
}

function todayYmd() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

function routeShortName(name: string) {
  return name.replace(/^Route\s*/i, "Route ");
}

export function alertSetupSubmitLabel(ready: boolean, cta: string, chooseDateCta: string): string {
  return ready ? cta : chooseDateCta;
}

export type AlertSetupStep = "routes" | "dates" | "party";

/** The first unfinished field, in form order; null when the form can submit. */
export function nextAlertSetupStep(input: { routeCount: number; dateCount: number; partySize: string; maxPartySize: number }): AlertSetupStep | null {
  if (input.routeCount === 0) return "routes";
  if (input.dateCount === 0) return "dates";
  const party = Number(input.partySize);
  if (!Number.isInteger(party) || party < 1 || party > input.maxPartySize) return "party";
  return null;
}

export function AlertSetupFields({
  routeGroups,
  selectedRouteValues,
  dateValues,
  partySize,
  locale = DEFAULT_LOCALE,
  setupCopy,
  maxPartySize = 10,
  title = "Build your alert",
  description = "Choose routes, dates, and party size. You can review everything before paying.",
  cta = "Continue",
  ready,
  minDate = "",
  onToggleRoute,
  onToggleAllRoutes,
  onToggleGroupRoutes,
  onDateChange,
  onPartySizeChange,
  onPartySizeStep,
  footer,
  collapsible = false,
  simplified = false,
  addDateLabel,
  submitRef,
}: {
  routeGroups: AlertSetupFieldRouteGroup[];
  selectedRouteValues: string[];
  dateValues: string[];
  partySize: string;
  locale?: PlatformLocale;
  setupCopy?: AlertSetupCopy;
  maxPartySize?: number;
  title?: string;
  description?: string;
  cta?: string;
  ready: boolean;
  minDate?: string;
  onToggleRoute: (value: string) => void;
  onToggleAllRoutes: () => void;
  onToggleGroupRoutes: (group: AlertSetupFieldRouteGroup) => void;
  onDateChange: (index: number, value: string) => void;
  onPartySizeChange: (value: string) => void;
  onPartySizeStep: (delta: number) => void;
  footer?: ReactNode;
  collapsible?: boolean;
  simplified?: boolean;
  addDateLabel?: string;
  submitRef?: Ref<HTMLButtonElement>;
}) {
  const copy = setupCopy ?? defaultCopy(locale);
  const flatRoutes = useMemo(() => routeGroups.flatMap((group) => group.routes), [routeGroups]);
  const selectedRouteSet = useMemo(() => new Set(selectedRouteValues), [selectedRouteValues]);
  const selectedDateObjects = useMemo(
    () => dateValues.map((date) => parseYmdLocal(date)).filter((date): date is Date => Boolean(date)),
    [dateValues],
  );
  const allSelected = flatRoutes.length > 0 && selectedRouteValues.length === flatRoutes.length;
  const singleProduct = simplified && flatRoutes.length === 1 && selectedRouteSet.has(flatRoutes[0]!.value);
  const [extraDates, setExtraDates] = useState(0);
  const visibleDateCount = simplified ? Math.max(1 + extraDates, dateValues.reduce((last, value, index) => value ? index + 1 : last, 0)) : 3;

  const [expanded, setExpanded] = useState<Set<SectionKey>>(() => new Set());
  const toggleSection = (key: SectionKey) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const dateFormatter = useMemo(() => new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" }), [locale]);
  const selectedRouteNames = useMemo(
    () => flatRoutes.filter((route) => selectedRouteSet.has(route.value)).map((route) => routeShortName(route.name)),
    [flatRoutes, selectedRouteSet],
  );
  const routesSummary = allSelected
    ? copy.allRoutes
    : selectedRouteNames.length <= 2
      ? selectedRouteNames.join(", ")
      : copy.routesSelected.replace("{count}", String(selectedRouteNames.length));
  const datesSummary = selectedDateObjects.map((date) => dateFormatter.format(date)).join(", ");
  const partySummary = `${partySize} ${copy.tickets}`;

  // In collapsible mode each section starts collapsed to a summary; an empty
  // required field forces its section open so the user is never blocked by a hidden gap.
  const routesOpen = !collapsible || expanded.has("routes") || selectedRouteValues.length === 0;
  const datesOpen = !collapsible || expanded.has("dates") || selectedDateObjects.length === 0;
  const partyOpen = !collapsible || expanded.has("party");

  return (
    <>
      <div className="border-b border-border p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="brand-utility text-xs font-bold tracking-wide text-brand-accent">{copy.eyebrow}</p>
            <h2 className="mt-2 text-2xl font-bold tracking-tight">{title}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{description}</p>
          </div>
        </div>
      </div>

      {singleProduct ? <div className="flex items-center gap-3 border-b border-border px-5 py-4 text-sm sm:px-6"><Check className="size-4 shrink-0 text-brand-accent" aria-hidden="true" /><span className="font-medium">{flatRoutes[0]!.name}</span></div> : <section data-setup-step="routes" className="scroll-mt-24 border-b border-border p-5 sm:p-6">
        <div className={cn("flex items-start justify-between gap-4", routesOpen && "mb-5")}>
          <div className="flex items-start gap-3">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-accent text-sm font-bold text-brand-accent-foreground">1</span>
            <div>
              <h3 className="font-bold">{copy.routesLabel}</h3>
              {routesOpen ? (
                <p className="mt-1 text-xs text-muted-foreground">{copy.backupHint}</p>
              ) : (
                <p className="mt-1 text-sm text-muted-foreground">{routesSummary}</p>
              )}
            </div>
          </div>
          {collapsible ? (
            <SectionToggle open={routesOpen} onClick={() => toggleSection("routes")} editLabel={copy.edit} doneLabel={copy.done} />
          ) : (
            <button type="button" onClick={onToggleAllRoutes} className="text-sm font-semibold text-brand-accent hover:underline">
              {allSelected ? copy.clearAll : copy.selectAll}
            </button>
          )}
        </div>

        {routesOpen && (
        <div className="space-y-5">
          {collapsible && (
            <div className="flex justify-end">
              <button type="button" onClick={onToggleAllRoutes} className="text-sm font-semibold text-brand-accent hover:underline">
                {allSelected ? copy.clearAll : copy.selectAll}
              </button>
            </div>
          )}
          {routeGroups.map((group) => (
            <div key={group.id} className="space-y-3">
              {routeGroups.length > 1 && <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-bold">
                  <Mountain className="size-4 text-muted-foreground" />
                  {group.name}
                </div>
                <button type="button" onClick={() => onToggleGroupRoutes(group)} className="text-xs font-semibold text-brand-accent hover:underline">
                  {group.routes.every((route) => selectedRouteSet.has(route.value)) ? copy.clearAll : copy.selectGroup}
                </button>
              </div>}
              <div className={cn("grid gap-3", simplified ? "sm:grid-cols-2" : "md:grid-cols-2")}>
                {group.routes.map((route) => {
                  const checked = selectedRouteSet.has(route.value);
                  return (
                    <label
                      key={route.value}
                      className={cn(
                        "flex min-h-14 cursor-pointer items-center gap-3 rounded-md border bg-card px-4 py-3 text-sm transition-colors hover:border-brand-accent/50 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-ring",
                        checked && "border-brand-accent bg-brand-accent/5",
                      )}
                    >
                      <span className={cn("flex size-4 items-center justify-center rounded border border-input", checked && "border-brand-accent bg-brand-accent text-brand-accent-foreground")}>
                        {checked && <Check className="size-3" />}
                      </span>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => onToggleRoute(route.value)}
                        className="sr-only"
                      />
                      <RouteIcon name={route.name} />
                      <span className="min-w-0 font-medium">{routeShortName(route.name)}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        )}
      </section>}

      <section data-setup-step="dates" className="scroll-mt-24 border-b border-border p-5 sm:p-6">
        <div className={cn("flex items-start justify-between gap-4", datesOpen && "mb-4")}>
          <div className="flex items-start gap-3">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-accent text-sm font-bold text-brand-accent-foreground">{singleProduct ? 1 : 2}</span>
            <div>
              <h3 className="font-bold">{copy.datesLabel}</h3>
              {datesOpen ? (
                <p className="mt-1 text-xs text-muted-foreground">{copy.datesHint}</p>
              ) : (
                <p className="mt-1 text-sm text-muted-foreground">{datesSummary}</p>
              )}
            </div>
          </div>
          {collapsible && (
            <SectionToggle open={datesOpen} onClick={() => toggleSection("dates")} editLabel={copy.edit} doneLabel={copy.done} />
          )}
        </div>
        {datesOpen && (
        <div className={cn("grid gap-3", !simplified && "md:grid-cols-3")}>
          {[0, 1, 2].slice(0, visibleDateCount).map((idx) => (
            <DateField
              key={idx}
              name="date"
              label={idx === 0 ? copy.travelDate : `${copy.backupDate} ${idx}`}
              placeholder={copy.pickDate}
              value={parseYmdLocal(dateValues[idx] ?? "")}
              onChange={(next) => onDateChange(idx, toYmd(next))}
              disabledBefore={parseYmdLocal(minDate)}
              preferredMonth={parseYmdLocal(dateValues[idx - 1] || dateValues[0] || "")}
              chosenDates={selectedDateObjects}
              locale={locale}
            />
          ))}
          {simplified && visibleDateCount < 3 && addDateLabel && <button type="button" onClick={() => setExtraDates(visibleDateCount)} className="flex min-h-11 items-center gap-2 text-sm font-semibold text-brand-accent hover:underline"><Plus className="size-4" aria-hidden="true" />{addDateLabel}</button>}
        </div>
        )}
      </section>

      <section data-setup-step="party" className="scroll-mt-24 border-b border-border p-5 sm:p-6">
        <div className={cn("flex items-start justify-between gap-4", partyOpen && "mb-4")}>
          <div className="flex items-start gap-3">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-accent text-sm font-bold text-brand-accent-foreground">{singleProduct ? 2 : 3}</span>
            <div>
              <h3 className="font-bold">{copy.partyLabel}</h3>
              {partyOpen ? (
                <p className="mt-1 text-xs text-muted-foreground">{copy.partyHint}</p>
              ) : (
                <p className="mt-1 text-sm text-muted-foreground">{partySummary}</p>
              )}
            </div>
          </div>
          {collapsible && (
            <SectionToggle open={partyOpen} onClick={() => toggleSection("party")} editLabel={copy.edit} doneLabel={copy.done} />
          )}
        </div>
        {partyOpen && (
        <div className="flex items-center gap-3">
          <div className="inline-flex overflow-hidden rounded-md border border-input bg-card">
            <button type="button" className="flex size-10 items-center justify-center hover:bg-muted" onClick={() => onPartySizeStep(-1)} aria-label={`${copy.partyLabel}: −`}>
              <Minus className="size-4" />
            </button>
            <input
              name="partySize"
              type="number"
              min={1}
              max={maxPartySize}
              value={partySize}
              onChange={(event) => onPartySizeChange(event.target.value)}
              inputMode="numeric"
              aria-label={copy.partyLabel}
              className="h-10 w-16 border-x border-input bg-card text-center text-sm font-bold outline-none"
            />
            <button type="button" className="flex size-10 items-center justify-center hover:bg-muted" onClick={() => onPartySizeStep(1)} aria-label={`${copy.partyLabel}: +`}>
              <Plus className="size-4" />
            </button>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="size-4" />
            {copy.tickets}
          </div>
        </div>
        )}
      </section>

      {footer ?? (
        <div className={cn("grid gap-4 p-5 sm:p-6", !simplified && "sm:grid-cols-[220px_1fr] sm:items-center")}>
          <div className="flex items-start gap-3 text-sm">
            <ShieldCheck className="mt-0.5 size-4 text-muted-foreground" />
            <div>
              <p className="font-semibold">{copy.delivery}</p>
              <p className="text-xs text-muted-foreground">{copy.reviewBeforePayment}</p>
            </div>
          </div>
          <div>
            {/* Never disabled: submitting an unfinished form moves the visitor to the missing field. */}
            <Button ref={submitRef} type="submit" aria-disabled={!ready} className={cn("h-11 w-full bg-brand-action text-brand-action-foreground hover:bg-brand-action/90", !ready && "opacity-80")}>
              {alertSetupSubmitLabel(selectedDateObjects.length > 0, cta, copy.chooseDateCta)}
              <ArrowRight className="size-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default function AlertSetupForm({
  routeGroups,
  locale = DEFAULT_LOCALE,
  title = "Build your alert",
  description = "Choose routes, dates, and party size. You can review everything before paying.",
  cta = "Continue",
  defaultPartySize = 2,
  defaultRouteSlugs,
  attractionId,
  setupCopy,
  maxPartySize = 10,
  simplified = false,
  addDateLabel,
}: {
  routeGroups: AlertSetupRouteGroup[];
  locale?: PlatformLocale;
  title?: string;
  description?: string;
  cta?: string;
  defaultPartySize?: number;
  defaultRouteSlugs?: readonly string[];
  attractionId?: AttractionId;
  setupCopy?: AlertSetupCopy;
  maxPartySize?: number;
  simplified?: boolean;
  addDateLabel?: string;
}) {
  const flatRoutes = useMemo(
    () =>
      routeGroups.flatMap((group) =>
        group.routes.map((route) => ({
          ...route,
          groupName: group.name,
        })),
      ),
    [routeGroups],
  );
  const fieldRouteGroups = useMemo<AlertSetupFieldRouteGroup[]>(
    () =>
      routeGroups.map((group) => ({
        id: group.id,
        name: group.name,
        routes: group.routes.map((route) => ({
          value: route.param,
          name: route.name,
        })),
      })),
    [routeGroups],
  );
  const [routeSlugs, setRouteSlugs] = useState(() => {
    const available = new Set(flatRoutes.map((route) => route.param));
    const requested = (defaultRouteSlugs ?? []).map((slug) => slug.trim()).filter((slug) => slug && available.has(slug));
    return requested.length > 0 ? requested : defaultSignupRouteSlugs(flatRoutes);
  });
  const [partySize, setPartySize] = useState(String(defaultPartySize));
  const [dates, setDates] = useState(["", "", ""]);
  const formStartedRef = useRef(false);
  const readyTrackedRef = useRef(false);
  const submittedRef = useRef(false);
  const formRef = useRef<HTMLFormElement | null>(null);
  const submitRef = useRef<HTMLButtonElement | null>(null);
  const [minDate, setMinDate] = useState("");
  const [formInView, setFormInView] = useState(false);
  const [submitInView, setSubmitInView] = useState(false);
  const selectedDates = dates.filter(Boolean);
  const selectedRouteSet = useMemo(() => new Set(routeSlugs), [routeSlugs]);
  const selectedRoutes = flatRoutes.filter((route) => selectedRouteSet.has(route.param));
  const targetCount = selectedRoutes.length * selectedDates.length;
  const missingStep = nextAlertSetupStep({ routeCount: routeSlugs.length, dateCount: selectedDates.length, partySize, maxPartySize });
  const ready = missingStep === null;
  const submitLabel = alertSetupSubmitLabel(selectedDates.length > 0, cta, (setupCopy ?? defaultCopy(locale)).chooseDateCta);

  useElementViewTracking({
    ref: formRef,
    eventName: "view_alert_setup",
    props: {
      attraction_id: attractionId,
      location: "pre_signup_setup",
      route_count: selectedRoutes.length,
      dates_count: selectedDates.length,
      target_count: targetCount,
      party_size: Number(partySize) || 0,
      ready,
    },
  });

  useAlertSetupAbandonment({
    startedRef: formStartedRef,
    submittedRef,
    snapshot: {
      location: "pre_signup_setup",
      ready,
      routeCount: selectedRoutes.length,
      datesCount: selectedDates.length,
      targetCount,
      partySize: Number(partySize) || 0,
    },
  });

  function trackSetupStart(action: string) {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    trackEvent("start_alert_setup", {
      attraction_id: attractionId,
      location: "pre_signup_setup",
      action,
      route_count: selectedRoutes.length,
      dates_count: selectedDates.length,
      target_count: targetCount,
      party_size: Number(partySize) || 0,
    });
  }

  useEffect(() => {
    const timeout = window.setTimeout(() => setMinDate(todayYmd()), 0);
    return () => window.clearTimeout(timeout);
  }, []);

  // The mobile action bar shows while the form is on screen but its own
  // submit button is not, so the next step is always one tap away.
  useEffect(() => {
    const form = formRef.current;
    const submit = submitRef.current;
    if (!form || !submit || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === form) setFormInView(entry.isIntersecting);
        if (entry.target === submit) setSubmitInView(entry.isIntersecting);
      }
    });
    observer.observe(form);
    observer.observe(submit);
    return () => observer.disconnect();
  }, []);

  function guideToStep(step: AlertSetupStep) {
    const section = formRef.current?.querySelector<HTMLElement>(`[data-setup-step="${step}"]`);
    if (!section) return;
    trackEvent("alert_setup_guided", { attraction_id: attractionId, location: "pre_signup_setup", step });
    section.scrollIntoView({ behavior: "smooth", block: "start" });
    if (step === "dates") section.querySelector<HTMLButtonElement>("button[aria-haspopup]")?.click();
    else if (step === "party") section.querySelector<HTMLInputElement>("input[name='partySize']")?.focus();
  }

  useEffect(() => {
    if (!ready || readyTrackedRef.current) return;
    readyTrackedRef.current = true;
    trackEvent("alert_setup_ready", {
      attraction_id: attractionId,
      location: "pre_signup_setup",
      route_count: selectedRoutes.length,
      dates_count: selectedDates.length,
      target_count: targetCount,
      party_size: Number(partySize) || 0,
    });
  }, [attractionId, partySize, ready, selectedDates.length, selectedRoutes.length, targetCount]);

  function toggleRoute(slug: string) {
    trackSetupStart("route_toggle");
    setRouteSlugs((prev) => {
      if (prev.includes(slug)) return prev.filter((value) => value !== slug);
      return [...prev, slug];
    });
  }

  function toggleAllRoutes() {
    trackSetupStart("select_all_routes");
    setRouteSlugs((prev) => (prev.length === flatRoutes.length ? [] : flatRoutes.map((route) => route.param)));
  }

  function toggleGroupRoutes(group: AlertSetupFieldRouteGroup) {
    trackSetupStart("select_group_routes");
    const groupParams = group.routes.map((route) => route.value);
    setRouteSlugs((prev) => {
      const allInGroupSelected = groupParams.every((param) => prev.includes(param));
      if (allInGroupSelected) {
        return prev.filter((value) => !groupParams.includes(value));
      }
      const set = new Set(prev);
      groupParams.forEach((param) => set.add(param));
      return Array.from(set);
    });
  }

  function updateDateAt(idx: number, value: string) {
    trackSetupStart(idx === 0 ? "travel_date" : "backup_date");
    setDates((prev) => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  }

  function updateParty(delta: number) {
    trackSetupStart("party_size");
    setPartySize((prev) => String(Math.min(maxPartySize, Math.max(1, (Number(prev) || 1) + delta))));
  }

  return (
    <form
      ref={formRef}
      action="/app/builder"
      method="GET"
      className="min-w-0 overflow-hidden rounded-lg border border-border bg-card"
      onSubmit={(event) => {
        trackEvent("click_create_alert", {
          attraction_id: attractionId,
          location: "pre_signup_setup",
          route_count: selectedRoutes.length,
          dates_count: selectedDates.length,
          target_count: targetCount,
          party_size: Number(partySize) || 0,
        });
        event.preventDefault();
        if (missingStep) {
          guideToStep(missingStep);
          return;
        }
        submittedRef.current = true;
        window.location.assign(
          buildLoginSetupPath({
            routeSlugs,
            dates: selectedDates,
            partySize,
            locale,
            attractionId,
          }),
        );
      }}
    >
      <input type="hidden" name="locale" value={locale} />
      {attractionId ? <input type="hidden" name="attraction" value={attractionId} /> : null}
      {routeSlugs.map((slug) => (
        <input key={slug} type="hidden" name="routeSlug" value={slug} />
      ))}

      <AlertSetupFields
        routeGroups={fieldRouteGroups}
        selectedRouteValues={routeSlugs}
        dateValues={dates}
        partySize={partySize}
        locale={locale}
        setupCopy={setupCopy}
        maxPartySize={maxPartySize}
        title={title}
        description={description}
        cta={cta}
        ready={ready}
        simplified={simplified}
        addDateLabel={addDateLabel}
        minDate={minDate}
        onToggleRoute={toggleRoute}
        onToggleAllRoutes={toggleAllRoutes}
        onToggleGroupRoutes={toggleGroupRoutes}
        onDateChange={updateDateAt}
        onPartySizeChange={(value) => {
          trackSetupStart("party_size");
          setPartySize(value);
        }}
        onPartySizeStep={updateParty}
        submitRef={submitRef}
      />
      {formInView && !submitInView ? (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 shadow-[0_-8px_24px_rgba(0,0,0,0.08)] backdrop-blur sm:hidden">
          <Button type="submit" aria-disabled={!ready} className="h-12 w-full bg-brand-action text-base text-brand-action-foreground hover:bg-brand-action/90">
            {submitLabel}
            <ArrowRight className="size-4" />
          </Button>
        </div>
      ) : null}
    </form>
  );
}
